import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { listTemplates, saveTemplate } from '../lib/savers';
import type { Attachment } from '../lib/db';
import { nanoid } from 'nanoid';
import { supabaseEnabled, supabase } from '../lib/supabase';
import { publicTemplates } from '../data/publicTemplates';

/**
 * ==========================================================================
 * SUPABASE BACKEND SCHEMA FOR MULTI-CATEGORY TEMPLATES
 * ==========================================================================
 * Run this SQL script in your Supabase SQL Editor to support public templates and mod approval:
 * 
 * create table public.templates (
 *   id uuid default gen_random_uuid() primary key,
 *   user_id uuid references auth.users(id) on delete cascade,
 *   name text not null,
 *   prompt text not null,
 *   category text not null, -- 'agents', 'crypto', 'characters', 'scenes', 'film', 'code', 'robotics', 'games & worlds'
 *   tags text[] default '{}',
 *   code text,
 *   deploy_command text,
 *   is_public boolean default false,
 *   status text default 'pending', -- 'pending' | 'approved' | 'rejected'
 *   start_frame text, -- path to file in bucket
 *   end_frame text,   -- path to file in bucket
 *   refs text[] default '{}', -- paths to assets in bucket
 *   created_at timestamp with time zone default timezone('utc'::text, now()) not null,
 *   updated_at timestamp with time zone default timezone('utc'::text, now()) not null
 * );
 * 
 * -- Row Level Security Setup:
 * alter table public.templates enable row level security;
 * 
 * -- SELECT POLICY (Anyone can view approved templates)
 * create policy "Anyone can view approved public templates" on public.templates
 *   for select using (is_public = true and status = 'approved');
 * 
 * -- SELECT POLICY (Users can view their own templates)
 * create policy "Users can view own templates" on public.templates
 *   for select using (auth.uid() = user_id);
 * 
 * -- INSERT POLICY (Users can create templates)
 * create policy "Users can insert own templates" on public.templates
 *   for insert with check (auth.uid() = user_id);
 * 
 * -- UPDATE POLICY (Users can update their own templates - except status)
 * create policy "Users can update own templates" on public.templates
 *   for update using (auth.uid() = user_id);
 * 
 * -- DELETE POLICY (Users can delete their own templates)
 * create policy "Users can delete own templates" on public.templates
 *   for delete using (auth.uid() = user_id);
 * 
 * -- MODERATION AND APPROVAL:
 * -- Mods/Admins can easily approve a template via:
 * -- UPDATE public.templates SET status = 'approved' WHERE id = '<template_id>';
 */

type Scope = 'you' | 'public';

interface SaverPanelProps {
  onClose(): void;
  onInsert(prompt: string, refs: Attachment[]): void;
  category?: string;
}

export default function SaverPanel({ onClose, onInsert, category = 'characters' }: SaverPanelProps) {
  const [scope, setScope] = useState<Scope>('you');
  const [items, setItems] = useState<any[]>([]);
  const [cloudPublicTemplates, setCloudPublicTemplates] = useState<any[]>([]);
  
  // Adaptive Form States
  const [name, setName] = useState('');
  const [seed, setSeed] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [files, setFiles] = useState<Attachment[]>([]); // general refs
  const [startFrame, setStartFrame] = useState<Attachment | null>(null);
  const [endFrame, setEndFrame] = useState<Attachment | null>(null);
  const [code, setCode] = useState('');
  const [deployCommand, setDeployCommand] = useState('');
  const [isPublicRequest, setIsPublicRequest] = useState(false);
  const [saving, setSaving] = useState(false);

  async function refresh() {
    // 1. Fetch user local templates for that category
    const userLocal = await listTemplates(category);
    setItems(userLocal);

    // 2. Fetch cloud-synced public approved templates if supabase is configured
    if (supabaseEnabled && supabase) {
      try {
        const { data } = await supabase
          .from('templates')
          .select('*')
          .eq('category', category)
          .eq('is_public', true)
          .eq('status', 'approved');
        if (data) {
          setCloudPublicTemplates(data);
        }
      } catch (err) {
        console.warn('Unable to reach Supabase templates table:', err);
      }
    }
  }

  useEffect(() => {
    refresh();
  }, [category]);

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const arr: Attachment[] = [];
    for (const f of Array.from(e.target.files ?? [])) {
      arr.push({ id: nanoid(), kind: 'file', mime: f.type, blob: f, name: f.name });
    }
    setFiles(prev => [...prev, ...arr]);
  }

  async function handleStartFrame(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) {
      setStartFrame({ id: nanoid(), kind: 'image', mime: f.type, blob: f, name: f.name });
    }
  }

  async function handleEndFrame(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) {
      setEndFrame({ id: nanoid(), kind: 'image', mime: f.type, blob: f, name: f.name });
    }
  }

  async function save() {
    if (!name.trim() || !seed.trim()) return;
    setSaving(true);
    try {
      const parsedTags = tagsInput
        .split(',')
        .map(t => t.trim())
        .filter(Boolean);

      await saveTemplate({
        name,
        seedPrompt: seed,
        category,
        tags: parsedTags,
        refs: files,
        code: ['code', 'agents', 'crypto', 'robotics'].includes(category) ? code : undefined,
        deployCommand: ['code', 'agents', 'crypto', 'robotics'].includes(category) ? deployCommand : undefined,
        startFrame: category === 'film' ? startFrame : null,
        endFrame: category === 'film' ? endFrame : null,
        isPublic: isPublicRequest
      });

      // Reset form fields
      setName('');
      setSeed('');
      setTagsInput('');
      setFiles([]);
      setStartFrame(null);
      setEndFrame(null);
      setCode('');
      setDeployCommand('');
      setIsPublicRequest(false);
      
      await refresh();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  // Choose display list based on Scope toggle
  const displayItems =
    scope === 'you'
      ? items
      : [
          ...publicTemplates.filter(t => t.category === category),
          ...cloudPublicTemplates.map(t => ({
            id: t.id,
            name: t.name,
            prompt: t.prompt,
            category: t.category,
            tags: t.tags || [],
            code: t.code,
            deployCommand: t.deploy_command,
            // dummy attachments for standard rendering helper
            refs: (t.refs || []).map((p: string) => ({ id: p, kind: 'file', name: p.split('/').pop() }))
          }))
        ];

  const isCodeType = ['code', 'agents', 'crypto', 'robotics'].includes(category);
  const isFilmType = category === 'film';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="modal-backdrop"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 15, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ type: "spring", damping: 30, stiffness: 350 }}
        className="modal glass glass--strong"
        style={{ width: '92%', maxWidth: '580px', maxHeight: 'calc(90dvh - 20px)', margin: '10px auto', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <button className="btn btn--ghost modal__close" onClick={onClose}>close</button>
        
        <div className="col" style={{ gap: '2px', marginBottom: '16px' }}>
          <h2 className="sc" style={{ margin: 0 }}>{category.toUpperCase()} TEMPLATES</h2>
          <div className="sc" style={{ fontSize: '9px', opacity: 0.5, letterSpacing: '0.1em' }}>
            CATEGORY: <span style={{ color: 'var(--accent)' }}>{category.replace('&', '/')}</span>
          </div>
        </div>

        {/* YOU / PUBLIC scope switcher */}
        <div className="row" style={{ marginBottom: 16, gap: '6px' }}>
          <button 
            className={`chip ${scope === 'you' ? 'is-active' : ''}`} 
            onClick={() => setScope('you')}
            style={{ padding: '4px 12px', fontSize: '10px' }}
          >
            YOU
          </button>
          <button 
            className={`chip ${scope === 'public' ? 'is-active' : ''}`} 
            onClick={() => setScope('public')}
            style={{ padding: '4px 12px', fontSize: '10px' }}
          >
            PUBLIC
          </button>
        </div>

        {/* CREATE / SAVE NEW TEMPLATE (Shown under YOU) */}
        {scope === 'you' && (
          <div className="col" style={{ gap: 10, marginBottom: 18, padding: '12px', borderRadius: '8px', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--line)' }}>
            <div className="sc" style={{ fontSize: '10px', opacity: 0.7, marginBottom: '4px' }}>Save current template</div>
            
            <div className="field">
              <label>name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. detective rin" />
            </div>

            <div className="field">
              <label>seed prompt</label>
              <textarea rows={2} value={seed} onChange={e => setSeed(e.target.value)} placeholder="short description; director will expand" />
            </div>

            <div className="field">
              <label>tags (comma-separated)</label>
              <input value={tagsInput} onChange={e => setTagsInput(e.target.value)} placeholder="e.g. cyberpunk, dark, 3d" />
            </div>

            {/* Film/Video Specific Fields */}
            {isFilmType && (
              <div className="col" style={{ gap: 10, borderLeft: '2px solid var(--accent)', paddingLeft: '8px', margin: '4px 0' }}>
                <div className="field">
                  <label>start frame reference</label>
                  <input type="file" accept="image/*" onChange={handleStartFrame} />
                  {startFrame && <span className="chip" style={{ fontSize: '8px', width: 'fit-content' }}>{startFrame.name}</span>}
                </div>
                <div className="field">
                  <label>end frame reference</label>
                  <input type="file" accept="image/*" onChange={handleEndFrame} />
                  {endFrame && <span className="chip" style={{ fontSize: '8px', width: 'fit-content' }}>{endFrame.name}</span>}
                </div>
              </div>
            )}

            {/* Code/Agents/Crypto/Robotics Specific Fields */}
            {isCodeType && (
              <div className="col" style={{ gap: 10, borderLeft: '2px solid var(--accent)', paddingLeft: '8px', margin: '4px 0' }}>
                <div className="field">
                  <label>source code / configuration</label>
                  <textarea rows={4} value={code} onChange={e => setCode(e.target.value)} placeholder="// Paste your deployment/gait script/smart contract here" style={{ fontFamily: 'var(--font-mono)', fontSize: '11px' }} />
                </div>
                <div className="field">
                  <label>deploy / execution command</label>
                  <input value={deployCommand} onChange={e => setDeployCommand(e.target.value)} placeholder="e.g. npx hardhat run deploy.js" style={{ fontFamily: 'var(--font-mono)', fontSize: '11px' }} />
                </div>
              </div>
            )}

            {/* Multiple files / sheets field for references */}
            <div className="field">
              <label>{isCodeType ? 'reference files / assets' : 'reference images / sheets'}</label>
              <input type="file" accept="*/*" multiple onChange={handleFiles} />
            </div>
            {files.length > 0 && (
              <div className="row row--wrap" style={{ gap: '4px' }}>
                {files.map(f => (
                  <span key={f.id} className="chip" style={{ fontSize: '8px' }}>{f.name}</span>
                ))}
              </div>
            )}

            {/* Request template to be public toggle */}
            <div className="row" style={{ gap: '8px', alignItems: 'center', marginTop: '4px' }}>
              <input 
                type="checkbox" 
                id="share_public_cb" 
                checked={isPublicRequest} 
                onChange={e => setIsPublicRequest(e.target.checked)} 
                style={{ width: '14px', height: '14px', accentColor: 'var(--accent)' }}
              />
              <label htmlFor="share_public_cb" style={{ fontSize: '10px', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Share template as PUBLIC (sends to mod for approval)
              </label>
            </div>

            <div className="row" style={{ justifyContent: 'flex-end', marginTop: '4px' }}>
              <button className="btn btn--primary" disabled={saving || !name.trim() || !seed.trim()} onClick={save}>
                {saving ? 'saving…' : 'save'}
              </button>
            </div>
          </div>
        )}

        <hr className="divider" style={{ margin: '14px 0' }} />

        {/* LISTING TEMPLATE ITEMS */}
        <div className="col" style={{ gap: 10 }}>
          {displayItems.length === 0 && (
            <div className="col" style={{ alignItems: 'center', padding: '16px 0', color: 'var(--fg-dim)', fontSize: 12 }}>
              nothing saved in {category} yet.
            </div>
          )}
          {displayItems.map(it => (
            <div key={it.id} className="glass glass--flat col" style={{ padding: 12, gap: 6, borderRadius: '8px' }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <span className="sc font-semibold" style={{ fontSize: 12, color: 'var(--fg)' }}>{it.name}</span>
                {it.isPublic && (
                  <span style={{ fontSize: '8px', color: 'var(--accent)', border: '1px solid var(--accent)', padding: '1px 4px', borderRadius: '3px', textTransform: 'uppercase' }}>
                    Shared (Pending)
                  </span>
                )}
              </div>

              {/* Tag items */}
              {it.tags && it.tags.length > 0 && (
                <div className="row row--wrap" style={{ gap: '4px' }}>
                  {it.tags.map((tag: string) => (
                    <span key={tag} className="chip" style={{ fontSize: '8px', background: 'rgba(255,255,255,0.05)', borderColor: 'transparent', padding: '1px 5px' }}>
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Seed prompt */}
              <div style={{ fontSize: '11px', color: 'var(--fg-dim)', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                {it.prompt}
              </div>

              {/* Special displays: code types */}
              {it.code && (
                <details style={{ width: '100%', margin: '4px 0' }}>
                  <summary style={{ fontSize: '9px', textTransform: 'uppercase', cursor: 'pointer', outline: 'none', color: 'var(--accent)' }}>
                    Show Code Snippet ({it.code.split('\n').length} lines)
                  </summary>
                  <pre style={{ 
                    marginTop: '4px',
                    padding: '8px',
                    background: 'rgba(0,0,0,0.5)', 
                    border: '1px solid var(--line)', 
                    borderRadius: '4px', 
                    fontFamily: 'var(--font-mono)', 
                    fontSize: '10px', 
                    color: '#8be9fd',
                    overflowX: 'auto',
                    maxHeight: '140px'
                  }}>
                    <code>{it.code}</code>
                  </pre>
                </details>
              )}

              {/* Special displays: deploy commands */}
              {it.deployCommand && (
                <div className="row" style={{ gap: '4px', alignItems: 'center', background: 'rgba(0,0,0,0.2)', padding: '4px 6px', borderRadius: '4px', border: '1px solid var(--line)' }}>
                  <span style={{ fontSize: '8px', color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>Deploy:</span>
                  <code style={{ fontSize: '10px', color: '#50fa7b', fontFamily: 'var(--font-mono)' }}>{it.deployCommand}</code>
                </div>
              )}

              {/* Special displays: start & end frames for film category */}
              {(it.startFrameName || it.endFrameName) && (
                <div className="row" style={{ gap: '8px', fontSize: '9px', color: 'var(--fg-dim)' }}>
                  {it.startFrameName && <span>🟢 Start: {it.startFrameName}</span>}
                  {it.endFrameName && <span>🔴 End: {it.endFrameName}</span>}
                </div>
              )}

              {/* References count */}
              {it.refs && it.refs.length > 0 && (
                <div style={{ fontSize: '9px', color: 'var(--fg-dim)' }}>
                  📦 References: {it.refs.map((r: any) => r.name || 'document').join(', ')}
                </div>
              )}

              <div className="row" style={{ marginTop: 4, justifyContent: 'flex-end' }}>
                <button 
                  className="btn btn--ghost" 
                  style={{ fontSize: '10px', padding: '3px 8px' }}
                  onClick={() => { 
                    onInsert(it.prompt + (it.code ? `\n\nCode snippet:\n\`\`\`\n${it.code}\n\`\`\`` : ''), it.refs || []); 
                    onClose(); 
                  }}
                >
                  use template →
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {/* Backend config instructions block for information parity */}
        {scope === 'public' && (
          <div style={{ padding: '6px 10px', border: '1px dashed rgba(255,220,100,0.2)', backgroundColor: 'rgba(255,220,100,0.01)', borderRadius: '6px', marginTop: '20px', fontSize: '9px', color: 'var(--fg-dim)', lineHeight: '1.4' }}>
            🔔 <strong>Database Mod Info:</strong> Templates submitted for review are instantly written to the Supabase <code>templates</code> table as <code>status = 'pending'</code>. A Moderator or Admin can easily approve a submission by changing the status to <code>approved</code> on their dashboard to make it publicly available around the world.
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

